import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { SITE_ID, SITE_SHORT_NAME } from '@/config/site';

interface AgeGateProps {
  minAge: number;
  mode: 'checkbox' | 'dob';
}

interface StoredAck {
  acknowledged: boolean;
  mode: 'checkbox' | 'dob';
  minAge: number;
  ts: string;
}

function storageKey(): string {
  return `rumio:age_gate:${SITE_ID}`;
}

function readAck(): StoredAck | null {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.acknowledged === true) {
      return parsed as StoredAck;
    }
  } catch {
    /* swallow corrupt JSON */
  }
  return null;
}

function writeAck(mode: 'checkbox' | 'dob', minAge: number): void {
  try {
    localStorage.setItem(
      storageKey(),
      JSON.stringify({
        acknowledged: true,
        mode,
        minAge,
        ts: new Date().toISOString(),
      } satisfies StoredAck),
    );
  } catch {
    /* swallow quota / privacy-mode errors */
  }
}

export default function AgeGate({ minAge, mode }: AgeGateProps) {
  const [acknowledged, setAcknowledged] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = readAck();
    if (!stored) return false;
    // If the tenant raised minAge after a prior ack, require a fresh ack.
    return stored.minAge >= minAge;
  });
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (acknowledged) {
      document.body.classList.remove('age-gate-open');
    } else {
      document.body.classList.add('age-gate-open');
    }
    return () => document.body.classList.remove('age-gate-open');
  }, [acknowledged]);

  if (acknowledged) return null;

  const handleEnter = () => {
    if (!checked) return;
    writeAck(mode, minAge);
    setAcknowledged(true);
  };

  const handleLeave = () => {
    // Redirect to a neutral page. Using the well-known Google.com keeps the
    // user out of the experience without us having to host an "exit" page.
    try {
      window.location.href = 'https://www.google.com/';
    } catch {
      /* SSR / sandbox — nothing to do */
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/85 backdrop-blur-sm"
    >
      <div className="max-w-md w-full mx-4 bg-surface rounded-2xl shadow-2xl p-6 sm:p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-700 mb-4">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <h2 id="age-gate-title" className="text-xl sm:text-2xl font-bold text-ink mb-2">
          Welcome to {SITE_SHORT_NAME}
        </h2>
        <p className="text-sm text-ink-soft mb-6">
          This is a play-for-fun social casino. Virtual coins and gems are not
          redeemable for cash, prizes, or anything of real-world value. You
          must be {minAge} or older to play.
        </p>

        {mode === 'checkbox' && (
          <label className="flex items-start gap-3 text-left text-sm text-ink-soft bg-surface-soft border border-line rounded-lg p-3 mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-amber-500"
              aria-describedby="age-gate-title"
            />
            <span>
              I am {minAge} or older and I accept the{' '}
              <a href="/page/terms-of-service" className="underline">terms</a>{' '}and{' '}
              <a href="/page/responsible-play" className="underline">responsible play</a>{' '}policies.
            </span>
          </label>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleEnter}
            disabled={mode === 'checkbox' ? !checked : false}
            className="flex-1 px-4 py-2.5 bg-amber-400 text-ink font-semibold rounded-full hover:bg-amber-300 transition-colors disabled:bg-surface-muted disabled:text-ink-muted disabled:cursor-not-allowed"
          >
            Enter
          </button>
          <button
            type="button"
            onClick={handleLeave}
            className="flex-1 px-4 py-2.5 bg-surface border border-line-strong text-ink-soft font-medium rounded-full hover:bg-surface-soft transition-colors"
          >
            Leave
          </button>
        </div>

        <p className="mt-4 text-[11px] text-ink-muted">
          Need help? Call <a href="tel:18004262537" className="underline">1-800-GAMBLER</a> (US).
        </p>
      </div>
    </div>
  );
}
